import { createColumnHelper, type SortingState } from '@tanstack/react-table';
import { Loader2, MoreHorizontal, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useCreateUser } from '~entities/users/api/create-user';
import { useGetUserRoles } from '~entities/users/api/get-user-roles';
import type { User } from '~entities/users/model/types';

import {
  type GHLUser,
  useGetGHLUsers,
} from '~shared/api/get-ghl-location-users';
import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

// Create a column helper
const columnHelper = createColumnHelper<GHLUser>();

export const GHLUsersTable = () => {
  const { data: ghlUsersData, isLoading: isGHLUsersLoading } = useGetGHLUsers();
  const { data: enrollioRolesData, isLoading: isEnrollioRolesLoading } =
    useGetUserRoles();
  const { mutate: createUser, isPending: isCreatingUser } = useCreateUser();

  const [searchQuery, setSearchQuery] = useState('');
  // const [isLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedUser, setSelectedUser] = useState<GHLUser | null>(null);
  const [selectedRole, setSelectedRole] = useState('');

  // Filter users based on search query
  const filteredUsers =
    ghlUsersData?.filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.role.toLowerCase().includes(searchLower)
      );
    }) ?? [];

  const enrollioRoles =
    enrollioRolesData?.map((role) => {
      return { name: role.name, id: role.id ?? '' };
    }) ?? [];

  const handleOpenAddToEnrollioModal = (user: GHLUser) => {
    setSelectedUser(user);
    setSelectedRole('');
    setIsAddDialogOpen(true);
  };

  const handleAddUserToEnrollio = () => {
    if (!selectedUser || !selectedRole) {
      return;
    }

    // Split the name into firstName and lastName
    const nameParts = selectedUser.name.trim().split(/\s+/);
    const firstName = nameParts[0] ?? '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const user: User = {
      firstName,
      lastName,
      email: selectedUser.email,
    };

    createUser(
      {
        roleId: selectedRole,
        user,
        ghlUserId: selectedUser.id,
      },
      {
        onSuccess: () => {
          toast.success('User added to Enrollio portal');
        },
        onError: (error) => {
          // Handle error - you might want to show a toast notification here
          toast.error('Failed to create user');
          console.error('Failed to create user:', error);
        },
      },
    );

    setIsAddDialogOpen(false);
    setSelectedUser(null);
    setSelectedRole('');
  };

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Name'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4 font-medium'>{info.getValue()}</div>
        ),
      }),
      columnHelper.accessor('email', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Email'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => <div className='px-4'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('role', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Role'
            className='pl-4 text-black'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4'>
            {info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1)}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Actions'
            className='pl-4 text-right text-black'
          />
        ),
        cell: (info) => (
          <div className=''>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='mx-4 size-8 p-0'>
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => {
                    handleOpenAddToEnrollioModal(info.row.original);
                  }}
                >
                  <UserPlus className='mr-2 size-4' />
                  Add user to enrollio portal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      }),
    ],
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>CRM Users</CardTitle>
        <CardDescription>
          Manage CRM user accounts and permissions
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isGHLUsersLoading || isEnrollioRolesLoading || isCreatingUser ? (
          <div className='flex justify-center py-8'>
            <Loader2 className='size-10 animate-spin' />
          </div>
        ) : (
          <EnrollioTable
            ariaLabel='CRM users'
            totalLabel='CRM users'
            filtersLayout='inline'
            filterHeader={
              <Input
                placeholder='Search CRM users...'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                className='w-full md:w-72'
              />
            }
            columns={columns}
            data={filteredUsers}
            sorting={sorting}
            onSortingChange={setSorting}
            isUpdating={isEnrollioRolesLoading}
          />
        )}

        {/* Add to Enrollio Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className='sm:max-w-[425px]'>
            <DialogHeader>
              <DialogTitle>Add User to Enrollio Portal</DialogTitle>
              <DialogDescription>
                Add this CRM user to the Enrollio portal with appropriate
                permissions.
              </DialogDescription>
            </DialogHeader>

            <div className='grid gap-4 py-4'>
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label htmlFor='name' className='text-right'>
                  Name
                </Label>
                <Input
                  id='name'
                  value={selectedUser?.name ?? ''}
                  disabled
                  className='col-span-3'
                />
              </div>
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label htmlFor='email' className='text-right'>
                  Email
                </Label>
                <Input
                  id='email'
                  type='email'
                  value={selectedUser?.email ?? ''}
                  disabled
                  className='col-span-3'
                />
              </div>
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label htmlFor='ghlRole' className='text-right'>
                  CRM Role
                </Label>
                <Input
                  id='ghlRole'
                  value={
                    (selectedUser?.role.charAt(0) ?? '').toUpperCase() +
                    (selectedUser?.role.slice(1) ?? '')
                  }
                  disabled
                  className='col-span-3'
                />
              </div>
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label htmlFor='enrollioRole' className='text-right'>
                  Enrollio Role
                </Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className='col-span-3'>
                    <SelectValue placeholder='Select a role' />
                  </SelectTrigger>
                  <SelectContent>
                    {enrollioRoles.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setIsAddDialogOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                onClick={handleAddUserToEnrollio}
                disabled={!selectedRole}
              >
                Add to Enrollio
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
